import React, { useState, useEffect } from 'react';
import {
  useNotificationPreferences,
  useUpdateNotificationPreferences,
} from '@theraply/api-client';

interface PreferencesState {
  emailEnabled?: boolean;
  smsEnabled?: boolean;
  reminder24h?: boolean;
  reminder1h?: boolean;
  newBookingAlerts?: boolean;
}

export default function NotificationSettings() {
  const { data: preferences, isLoading, error } = useNotificationPreferences();
  const updatePreferences = useUpdateNotificationPreferences();

  const [localPreferences, setLocalPreferences] = useState<PreferencesState>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    if (preferences) {
      setLocalPreferences({
        emailEnabled: preferences.emailEnabled,
        smsEnabled: preferences.smsEnabled,
        reminder24h: preferences.reminder24h,
        reminder1h: preferences.reminder1h,
        newBookingAlerts: preferences.newBookingAlerts,
      });
    }
  }, [preferences]);

  useEffect(() => {
    if (preferences) {
      const changed =
        localPreferences.emailEnabled !== preferences.emailEnabled ||
        localPreferences.smsEnabled !== preferences.smsEnabled ||
        localPreferences.reminder24h !== preferences.reminder24h ||
        localPreferences.reminder1h !== preferences.reminder1h ||
        localPreferences.newBookingAlerts !== preferences.newBookingAlerts;
      setHasChanges(changed);
      if (changed) setSaveSuccess(false);
    }
  }, [localPreferences, preferences]);

  const handleToggle = (field: keyof PreferencesState) => {
    setLocalPreferences((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleSave = async () => {
    try {
      await updatePreferences.mutateAsync(localPreferences);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to save preferences:', err);
    }
  };

  const handleReset = () => {
    if (preferences) {
      setLocalPreferences({
        emailEnabled: preferences.emailEnabled,
        smsEnabled: preferences.smsEnabled,
        reminder24h: preferences.reminder24h,
        reminder1h: preferences.reminder1h,
        newBookingAlerts: preferences.newBookingAlerts,
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="m-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
        Failed to load notification preferences. Please try again later.
      </div>
    );
  }

  const channelsDisabled = !localPreferences.emailEnabled && !localPreferences.smsEnabled;

  return (
    <div className="max-w-3xl mx-auto p-6">
      <div className="flex items-center mb-3">
        <svg className="w-8 h-8 mr-3 text-indigo-600" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
        </svg>
        <h1 className="text-2xl font-bold text-gray-900">Notification Settings</h1>
      </div>

      <p className="text-gray-500 mb-6">
        Control how and when you receive notifications about your sessions and bookings.
      </p>

      {saveSuccess && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700">
          Your notification preferences have been saved successfully.
        </div>
      )}

      {updatePreferences.isError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
          Failed to save preferences. Please try again.
        </div>
      )}

      <div className="space-y-6">
        {/* Notification Channels */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-1">Notification Channels</h2>
          <p className="text-sm text-gray-500 mb-4">Choose how you want to receive notifications.</p>

          <div className="space-y-4">
            <ToggleRow
              label="Email Notifications"
              description="Receive notifications via email"
              checked={localPreferences.emailEnabled ?? true}
              onChange={() => handleToggle('emailEnabled')}
            />
            <ToggleRow
              label="SMS Notifications"
              description="Receive text messages for important updates"
              checked={localPreferences.smsEnabled ?? true}
              onChange={() => handleToggle('smsEnabled')}
            />
          </div>
        </div>

        {/* Booking Alerts */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-1">Booking Alerts</h2>
          <p className="text-sm text-gray-500 mb-4">Get notified immediately when clients book sessions with you.</p>

          <ToggleRow
            label="New Booking Alerts"
            description="Receive instant notifications when a new booking is made"
            checked={localPreferences.newBookingAlerts ?? true}
            onChange={() => handleToggle('newBookingAlerts')}
            disabled={channelsDisabled}
          />

          {channelsDisabled && (
            <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
              Enable at least one notification channel to receive booking alerts.
            </div>
          )}
        </div>

        {/* Session Reminders */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-1">Session Reminders</h2>
          <p className="text-sm text-gray-500 mb-4">Choose when you want to be reminded about upcoming sessions.</p>

          <div className="space-y-4">
            <ToggleRow
              label="24-Hour Reminder"
              description="Get reminded one day before your session"
              checked={localPreferences.reminder24h ?? true}
              onChange={() => handleToggle('reminder24h')}
              disabled={channelsDisabled}
            />
            <ToggleRow
              label="1-Hour Reminder"
              description="Get reminded one hour before your session starts"
              checked={localPreferences.reminder1h ?? true}
              onChange={() => handleToggle('reminder1h')}
              disabled={channelsDisabled}
            />
          </div>

          {channelsDisabled && (
            <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
              Enable at least one notification channel to receive reminders.
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <button
            onClick={handleReset}
            disabled={!hasChanges || updatePreferences.isPending}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Reset
          </button>
          <button
            onClick={handleSave}
            disabled={!hasChanges || updatePreferences.isPending}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {updatePreferences.isPending ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ToggleRow({ label, description, checked, onChange, disabled = false }: {
  label: string;
  description: string;
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center justify-between">
      <div>
        <p className={`font-medium ${disabled ? 'text-gray-400' : 'text-gray-900'}`}>{label}</p>
        <p className={`text-sm ${disabled ? 'text-gray-300' : 'text-gray-500'}`}>{description}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={onChange}
        disabled={disabled}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
          checked ? 'bg-indigo-600' : 'bg-gray-200'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            checked ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );
}
